import { FC, useEffect } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import { useTranslation, Trans } from 'react-i18next';
import { Flex, FlexProps, Text, IconButton, Link, useTheme } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import styled from '@emotion/styled/macro';
import { useCookies } from 'react-cookie';
import { Loader } from 'components';
import { rem } from 'utils/fonts';
import { mq } from 'styles/breakpoints';
import { ReactComponent as MonsterPink } from 'assets/svgs/monster_rock.svg';
import { useDispatch, useSelector } from 'react-redux';
import { switchProfileTo } from 'modules/user/userSlice';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { RootState } from 'store';

interface CookieProps {
  isOpen?: boolean;
}

const CookieWrapper = styled(Flex)`
  max-width: calc(100% - 45px);
  position: fixed;
  bottom: 64px;
  z-index: 999;

  > svg {
    height: 65px;
    width: 57px;
    margin-right: 23px;
  }

  ${mq.mdMax} {
    left: 20px;
  }

  ${mq.md} {
    max-width: 525px;
    bottom: 40px;
    right: 50px;
  }
`;

const PinkBox = styled(Flex)`
  flex-direction: column;
  max-width: 460px;
  padding: 12px 45px 12px 21px;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    left: -6px;
    top: 10px;
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
  }
`;

const StyledIconButton = styled(IconButton)`
  background-color: transparent;
  height: 24px;
  min-width: 24px;
  max-width: 24px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;

  svg {
    width: 14px;
  }
`;

const NavItem = styled(Link)`
  text-decoration: underline;
`;

const CookieBar: FC<FlexProps & CookieProps> = ({ isOpen = false, ...props }) => {
  const { colors } = useTheme();
  const context = useSelector((state: RootState) => state.app.context);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const { t, ready } = useTranslation('common');
  const [cookies, setCookie] = useCookies(['chooose', 'chooose-cookies-consent-state']);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!cookies['chooose-cookies-consent-state']) {
      setCookie('chooose-cookies-consent-state', JSON.stringify({ tracking: true, targeting: true }), { path: '/', maxAge: 60 });
    }
  }, []);

  const onCloseIconClick = (): void => {
    !isOpen && setCookie('chooose', true, { path: '/', maxAge: 2.628e6 });
  };

  function handleCookieClick(): void {
    dispatch(switchProfileTo('Person'));
  }

  return (
    <>
      {!cookies.chooose || isOpen ? (
        <CookieWrapper {...props}>
          {context === 'mychooose' && <MonsterPink fill={colors.other.CTA} />}
          <PinkBox
            bg={isCorporateTheme ? 'white' : 'pink.50'}
            borderRadius="base"
            boxShadow={isCorporateTheme ? 'whiteBox' : 'cookieBar'}
            _after={{ borderRight: isCorporateTheme || context !== 'mychooose' ? 'none' : `6px solid ${colors.pink[50]}` }}
          >
            {!ready ? (
              <Loader small loaderDirection="right" />
            ) : (
              <>
                {!isCorporateTheme && (
                  <Text fontSize={rem(16)} lineHeight="130%" fontWeight="bold" color="other.CTA" textAlign="left" mb="2px">
                    {t('cookies.title')}
                  </Text>
                )}
                <Text fontSize={rem(16)} lineHeight="130%" fontWeight="normal" color="secondary.800" textAlign="left">
                  <Trans t={t} i18nKey="cookies.details">
                    By continuing to use this site you accept our use of
                    <NavItem as={NavLink} color="other.CTA" exact to="/settings/terms-and-conditions#cookies" onClick={handleCookieClick}>
                      cookies
                    </NavItem>
                    .
                  </Trans>
                </Text>
                <StyledIconButton
                  color="other.CTA"
                  _hover={{ bg: 'other.CTA', color: 'white' }}
                  aria-label={t('cookies.close')}
                  icon={<CloseIcon />}
                  onClick={onCloseIconClick}
                />
              </>
            )}
          </PinkBox>
        </CookieWrapper>
      ) : null}
    </>
  );
};

export default CookieBar;
